import { BudgetLimits } from '@/dashboards/CampaignPlanner/hooks/useBudgetLimits';
import { formatNumber } from '@/utils/numberUtils';
import { z } from 'zod';

const baseSchema = z
    .object({
        name: z.string().min(1, 'Name is required'),
        budget: z.number().min(1, 'Budget is required'),
        startDate: z.date({ required_error: 'Start date is required' }),
        endDate: z.date({ required_error: 'End date is required' }),
        optimizationBase: z.enum(['budget', 'media_kpi', 'total_kpi']),
    })
    .refine((data) => data.endDate >= data.startDate, {
        message: 'End date must be on or after start date',
        path: ['endDate'],
    });

export type CampaignFormData = z.infer<typeof baseSchema>;

export const makeSchema = (budgetLimits: BudgetLimits) =>
    baseSchema.superRefine((data, ctx) => {
        if (data.budget === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Budget cannot be zero',
                path: ['budget'],
            });
        }
        if (data.budget < budgetLimits.min) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Budget must be at least ${formatNumber(budgetLimits.min)}`,
                path: ['budget'],
            });
        }
        if (budgetLimits.max > 0 && data.budget > budgetLimits.max) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Budget must be at most ${formatNumber(budgetLimits.max)}`,
                path: ['budget'],
            });
        }
    });

export const STEP_LABELS = ['Name', 'Type', 'Channels', 'Dates', 'Target'] as const;
export const TOTAL_STEPS = STEP_LABELS.length;

export const stepFieldsMap: (keyof CampaignFormData)[][] = [
    ['name'],
    ['optimizationBase'],
    [],
    ['startDate', 'endDate'],
    ['budget'],
];
